import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Lightbulb, BookOpen } from 'lucide-react';
import { usePage } from '../hooks/useContent';

export function WhatIsPromptLiteracyPage() {
  const { page, loading, error } = usePage('what-is-prompt-literacy');

  if (loading) {
    return (
      <div className="container py-16">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/2 mb-4" />
          <div className="h-4 bg-muted rounded w-3/4 mb-8" />
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-5/6" />
            <div className="h-4 bg-muted rounded w-4/5" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Page not found</h1>
        <p className="text-muted-foreground">{error || 'This page could not be loaded.'}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{page.title} - Promptliteracy.ai</title>
        <meta name="description" content={page.description || 'Understanding the fundamentals of communicating effectively with AI systems'} />
        <meta property="og:title" content={`${page.title} - Promptliteracy.ai`} />
        <meta property="og:description" content={page.description || 'Understanding the fundamentals of communicating effectively with AI systems'} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="container py-8 max-w-4xl">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <Link to="/" className="text-muted-foreground link-underline">
            Home
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground font-medium">{page.title}</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-6 w-6 text-primary" />
            <Badge variant="secondary">Essential Guide</Badge>
          </div>
          <h1 className="text-4xl font-bold mb-4">{page.title}</h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            {page.description || 'Understanding the fundamentals of communicating effectively with AI systems'}
          </p>
        </div>

        {/* Quick Start Card */}
        <Card className="mb-8 border-primary/20 bg-primary/5 card-accent">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Quick Start Checklist
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Be Specific</h3>
                    <p className="text-sm text-muted-foreground">Say exactly what you want the AI to do</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Provide Context</h3>
                    <p className="text-sm text-muted-foreground">Give background information and explain your situation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Set Constraints</h3>
                    <p className="text-sm text-muted-foreground">Define length, style, format, or other requirements</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Iterate</h3>
                    <p className="text-sm text-muted-foreground">Refine your prompts based on the results you get</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">5</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Verify</h3>
                    <p className="text-sm text-muted-foreground">Check outputs carefully like a scientist</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border/40">
                  <Button asChild>
                    <Link to="/examples">
                      <BookOpen className="mr-2 h-4 w-4" />
                      See Examples
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="prose prose-gray max-w-none">
          <div className="text-lg leading-relaxed space-y-6">
            <p>
              <strong>Prompt literacy is the skill of turning intent into clear instructions that AI can follow.</strong> 
              It blends critical thinking, structure, and context to help you communicate effectively with AI systems.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Why It Matters</h2>
            <p>
              As AI becomes more integrated into our daily lives, the ability to communicate clearly with these systems 
              becomes as important as traditional literacy skills. Whether you're a student, educator, professional, 
              or simply curious about AI, prompt literacy helps you:
            </p>
            
            <ul className="space-y-2 my-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span>Get better, more relevant results from AI tools</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span>Save time by reducing back-and-forth iterations</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span>Think more critically about AI outputs</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span>Use AI as a more effective thinking partner</span>
              </li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Common Misconceptions</h2>
            <div className="bg-accent/50 border border-accent/60 rounded-lg p-4 my-6">
              <p className="font-medium mb-2">❌ "I just need to ask the AI nicely"</p>
              <p className="text-sm text-muted-foreground">
                While politeness doesn't hurt, AI responds better to clear, structured instructions than pleasantries.
              </p>
            </div>
            
            <div className="bg-accent/50 border border-accent/60 rounded-lg p-4 my-6">
              <p className="font-medium mb-2">❌ "Longer prompts are always better"</p>
              <p className="text-sm text-muted-foreground">
                Effective prompts are specific, not necessarily long. Quality over quantity.
              </p>
            </div>

            <div className="bg-accent/50 border border-accent/60 rounded-lg p-4 my-6">
              <p className="font-medium mb-2">❌ "AI will figure out what I mean"</p>
              <p className="text-sm text-muted-foreground">
                AI works best when you're explicit about your needs, context, and desired outcomes.
              </p>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Getting Started</h2>
            <p>
              Start small, iterate, and check results like a scientist. Begin with simple, clear requests and gradually 
              build more sophisticated prompting techniques as you gain experience.
            </p>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 my-6">
              <h3 className="font-semibold mb-3">Ready to practice?</h3>
              <p className="text-muted-foreground mb-4">
                Explore our library of proven prompt patterns and techniques with step-by-step explanations.
              </p>
              <Button asChild>
                <Link to="/examples">
                  Explore Examples
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}